import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  Box,
  Divider,
  Tooltip,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import { InboundEmailDTO } from 'src/types/application/InboundEmailDTO';
import useInboundEmails from 'src/api/core/inbound-emails/listInboundEmails';
import useGetInboundEmail from 'src/api/core/inbound-emails/getInboundEmail';
import useRetryPurchaseOrderExtraction from 'src/api/core/inbound-emails/retryPurchaseOrderExtraction';
import { useReprocessInboundEmail } from 'src/api/communications/useReprocessInboundEmail';
import { Link, useNavigate } from '@tanstack/react-router';
import PDFViewer from 'src/components/pdf-viewer/PDFViewer';
import { XlsxViewer } from '@/components/xlsx-viewer/XlsxViewer';
import { Button } from '@/components/ui/button';
import { useListInboundEmailWorkflowRuns } from 'src/api/rpc/hooks/communications/useEmailRules';
import useAuth from '@/api/auth/useAuth';
import { cn } from '@/lib/utils';

interface InboundEmailModalProps {
  inboundEmailId: string;
  emails: InboundEmailDTO[];
  onClose: () => void;
}

const InboundEmailModal: React.FC<InboundEmailModalProps> = ({
  inboundEmailId,
  emails,
  onClose,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [currentEmailId, setCurrentEmailId] = useState(inboundEmailId);
  const [currentEmail, setCurrentEmail] = useState<InboundEmailDTO | null>(
    null,
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [absolutePosition, setAbsolutePosition] = useState(0);
  const isPaginating = useRef(false);

  const workflowRunsQuery = useListInboundEmailWorkflowRuns(currentEmailId, {
    enabled: Boolean(currentUser?.isAdmin && currentEmailId),
  });

  // Fetch additional data for the selected email
  const { data: detailedEmail, isLoading: isLoadingDetails } =
    useGetInboundEmail(currentEmailId);

  // Retry purchase order extraction
  const retryPOExtraction = useRetryPurchaseOrderExtraction(currentEmailId);

  const handleRetryPOExtraction = async () => {
    await retryPOExtraction.mutateAsync({});
  };

  // Re-process entire email
  const reprocessEmail = useReprocessInboundEmail();

  const handleReprocessEmail = async () => {
    await reprocessEmail.mutateAsync(currentEmailId);
  };

  // Get the initial page based on the index in the provided emails array
  useEffect(() => {
    const index = emails.findIndex((email) => email.id === inboundEmailId);
    if (index !== -1) {
      setCurrentEmail(emails[index]);
      setCurrentIndex(index);
      setAbsolutePosition(index + 1); // 1-based position
    }
  }, [inboundEmailId, emails]);

  // Query to get data for next/previous pages when needed
  const { data, isLoading } = useInboundEmails(
    {
      page,
      pageSize,
    },
    true,
  );

  // Update the current email when navigating
  useEffect(() => {
    if (!isPaginating.current) {
      const index = emails.findIndex((email) => email.id === currentEmailId);
      if (index !== -1) {
        setCurrentEmail(emails[index]);
        setCurrentIndex(index);
        setAbsolutePosition((page - 1) * pageSize + index + 1);
      }
    }
  }, [currentEmailId, emails, page, pageSize]);

  // Handle selecting first/last item after page change
  useEffect(() => {
    if (data?.edges?.length && isPaginating.current) {
      const selectFirst = sessionStorage.getItem('selectFirstEmail');
      const selectLast = sessionStorage.getItem('selectLastEmail');

      if (selectFirst === 'true') {
        sessionStorage.removeItem('selectFirstEmail');
        const firstEmail = data.edges[0];
        setCurrentEmail(firstEmail);
        setCurrentIndex(0);
        setCurrentEmailId(firstEmail.id);
        setAbsolutePosition((page - 1) * pageSize + 1);
      } else if (selectLast === 'true') {
        sessionStorage.removeItem('selectLastEmail');
        const lastIndex = data.edges.length - 1;
        const lastEmail = data.edges[lastIndex];
        setCurrentEmail(lastEmail);
        setCurrentIndex(lastIndex);
        setCurrentEmailId(lastEmail.id);
        setAbsolutePosition((page - 1) * pageSize + lastIndex + 1);
      }

      // Reset pagination flag after data loads
      isPaginating.current = false;
    }
  }, [data?.edges, page, pageSize]);

  // Navigation with pagination support
  const navigateEmail = useCallback(
    (direction: 'prev' | 'next') => {
      if (!emails.length) return;

      const totalItems = emails.length;
      let newIndex;

      if (direction === 'prev') {
        if (currentIndex <= 0) {
          // If we're at the first item and there's a previous page
          if (page > 1) {
            // Go to the previous page and select the last item
            isPaginating.current = true;
            setPage(page - 1);
            // We'll select the last item of the new page when data loads
            sessionStorage.setItem('selectLastEmail', 'true');
            return;
          } else {
            // If no previous page, wrap to the end of current page
            newIndex = totalItems - 1;
          }
        } else {
          // Normal case - move to previous item
          newIndex = currentIndex - 1;
        }
      } else {
        // Next direction
        if (currentIndex >= totalItems - 1) {
          // If we're at the last item and there's a next page
          if (data?.pageInfo?.hasNextPage) {
            // Go to the next page and select the first item
            isPaginating.current = true;
            setPage(page + 1);
            // We'll select the first item of the new page when data loads
            sessionStorage.setItem('selectFirstEmail', 'true');
            return;
          } else {
            // If no next page, wrap to the beginning of current page
            newIndex = 0;
          }
        } else {
          // Normal case - move to next item
          newIndex = currentIndex + 1;
        }
      }

      setCurrentIndex(newIndex);
      setCurrentEmailId(emails[newIndex].id);
      setCurrentEmail(emails[newIndex]);
      setAbsolutePosition((page - 1) * pageSize + newIndex + 1);
    },
    [currentIndex, emails, page, pageSize, data?.pageInfo?.hasNextPage],
  );

  const handleNext = useCallback(() => {
    navigateEmail('next');
  }, [navigateEmail]);

  const handlePrevious = useCallback(() => {
    navigateEmail('prev');
  }, [navigateEmail]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handlePrevious();
      } else if (event.key === 'ArrowRight') {
        handleNext();
      } else if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleNext, handlePrevious, onClose]);

  // Use detailed email data if available, otherwise fall back to list data
  // Merge attachments from currentEmail if detailedEmail has none
  const emailToDisplay = detailedEmail
    ? {
        ...detailedEmail,
        attachments:
          (detailedEmail.attachments?.length ?? 0) > 0
            ? detailedEmail.attachments
            : currentEmail?.attachments || [],
      }
    : currentEmail;

  if (!emailToDisplay || isLoading) {
    return (
      <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Email Viewer</MDTypography>
            <IconButton onClick={onClose} size="small">
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </MDBox>
        </DialogContent>
      </Dialog>
    );
  }

  const hasExtractions =
    (detailedEmail?.extractionResults?.apInvoices?.length ?? 0) > 0 ||
    (detailedEmail?.extractionResults?.quoteSheets?.length ?? 0) > 0 ||
    (detailedEmail?.extractionResults?.purchaseOrders?.length ?? 0) > 0;

  const hasRelatedRecords =
    (detailedEmail?.relatedRecords?.salesOrders?.length ?? 0) > 0 ||
    (detailedEmail?.relatedRecords?.quoteSheets?.length ?? 0) > 0 ||
    (detailedEmail?.relatedRecords?.apInvoices?.length ?? 0) > 0;

  // Calculate the total count from page info
  const totalCount = data?.pageInfo?.totalCount || emails.length;

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDBox display="flex" alignItems="center" gap={1}>
            <MDTypography variant="h6">Email Viewer</MDTypography>
            <MDTypography variant="caption" color="text.secondary">
              {absolutePosition} of {totalCount}
            </MDTypography>
            <Tooltip title="Use left/right arrow keys to navigate">
              <Icon fontSize="small" color="action">
                keyboard
              </Icon>
            </Tooltip>
          </MDBox>
          <Box display="flex" gap={1}>
            <Tooltip title="Re-process Email">
              <span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReprocessEmail}
                  disabled={reprocessEmail.isPending}
                >
                  <Icon sx={{ fontSize: '1rem', mr: 0.5 }}>refresh</Icon>
                  Re-process
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Previous Email (←)">
              <span>
                <IconButton
                  onClick={handlePrevious}
                  size="small"
                  disabled={page === 1 && currentIndex === 0}
                >
                  <Icon>arrow_back</Icon>
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next Email (→)">
              <span>
                <IconButton
                  onClick={handleNext}
                  size="small"
                  disabled={
                    !data?.pageInfo?.hasNextPage &&
                    currentIndex === emails.length - 1
                  }
                >
                  <Icon>arrow_forward</Icon>
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <Icon>close</Icon>
            </IconButton>
          </Box>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox p={2}>
          <MDBox mb={2}>
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDTypography variant="h6" fontWeight="medium">
                {emailToDisplay.subject || '(No Subject)'}
              </MDTypography>
              {emailToDisplay.attachments &&
                emailToDisplay.attachments.length > 0 && (
                  <Chip
                    icon={
                      <Icon sx={{ fontSize: '1rem !important' }}>
                        attach_file
                      </Icon>
                    }
                    label={`${emailToDisplay.attachments.length} attachment${emailToDisplay.attachments.length !== 1 ? 's' : ''}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
            </MDBox>
          </MDBox>

          <MDBox mb={2}>
            <MDTypography variant="body2" fontWeight="regular">
              <strong>From:</strong> {emailToDisplay.from}
            </MDTypography>
            <MDTypography variant="body2" fontWeight="regular">
              <strong>To:</strong> {emailToDisplay.to}
            </MDTypography>
            {emailToDisplay.cc && (
              <MDTypography variant="body2" fontWeight="regular">
                <strong>CC:</strong> {emailToDisplay.cc}
              </MDTypography>
            )}
            {emailToDisplay.receivedByMailbox && (
              <MDTypography variant="body2" fontWeight="regular">
                <strong>Mailbox/UPN:</strong> {emailToDisplay.receivedByMailbox}
              </MDTypography>
            )}
            <MDTypography variant="body2" fontWeight="regular">
              <strong>Date:</strong>{' '}
              {new Date(emailToDisplay.createdAt).toLocaleString()}
            </MDTypography>
          </MDBox>

          {/* Downstream Extractions Section */}
          {isLoadingDetails && (
            <MDBox display="flex" alignItems="center" gap={1} mb={2}>
              <CircularProgress size={16} />
              <MDTypography variant="caption" color="text.secondary">
                Loading extraction details...
              </MDTypography>
            </MDBox>
          )}

          {!isLoadingDetails && (hasExtractions || hasRelatedRecords) && (
            <MDBox mb={2}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                  <MDTypography variant="subtitle2" fontWeight="medium">
                    Related Records & Extractions
                  </MDTypography>
                </AccordionSummary>
                <AccordionDetails>
                  <MDBox display="flex" flexDirection="column" gap={2}>
                    {/* Sales Orders */}
                    {detailedEmail?.relatedRecords?.salesOrders &&
                      detailedEmail.relatedRecords.salesOrders.length > 0 && (
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            color="success"
                          >
                            Sales Orders (
                            {detailedEmail.relatedRecords.salesOrders.length})
                          </MDTypography>
                          <MDBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                            {detailedEmail.relatedRecords.salesOrders.map(
                              (so) => (
                                <Chip
                                  key={so.id}
                                  label={`SO-${so.internalId} (${so.status})`}
                                  size="small"
                                  color="success"
                                  onClick={() =>
                                    navigate({
                                      to: '/sales-orders/$salesOrderId',
                                      params: { salesOrderId: so.id },
                                    })
                                  }
                                  sx={{ cursor: 'pointer' }}
                                />
                              ),
                            )}
                          </MDBox>
                        </MDBox>
                      )}

                    {/* Quote Sheets */}
                    {detailedEmail?.relatedRecords?.quoteSheets &&
                      detailedEmail.relatedRecords.quoteSheets.length > 0 && (
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            color="info"
                          >
                            Quote Sheets (
                            {detailedEmail.relatedRecords.quoteSheets.length})
                          </MDTypography>
                          <MDBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                            {detailedEmail.relatedRecords.quoteSheets.map(
                              (qs) => (
                                <Chip
                                  key={qs.id}
                                  label={`Quote Sheet (${new Date(qs.quoteDate || qs.createdAt).toLocaleDateString()})`}
                                  size="small"
                                  color="info"
                                  onClick={() =>
                                    navigate({
                                      to: '/price-sheet-inbox/$quoteSheetId',
                                      params: { quoteSheetId: qs.id },
                                    })
                                  }
                                  sx={{ cursor: 'pointer' }}
                                />
                              ),
                            )}
                          </MDBox>
                        </MDBox>
                      )}

                    {/* AP Invoices */}
                    {detailedEmail?.relatedRecords?.apInvoices &&
                      detailedEmail.relatedRecords.apInvoices.length > 0 && (
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            color="warning"
                          >
                            AP Invoices (
                            {detailedEmail.relatedRecords.apInvoices.length})
                          </MDTypography>
                          <MDBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                            {detailedEmail.relatedRecords.apInvoices.map(
                              (inv) => (
                                <Chip
                                  key={inv.id}
                                  label={`${inv.invoiceNumber} ($${typeof inv.totalAmount === 'number' ? inv.totalAmount.toFixed(2) : inv.totalAmount}) - ${inv.status}`}
                                  size="small"
                                  color="warning"
                                  onClick={() =>
                                    navigate({
                                      to: '/accounts-payable/invoices/$invoiceId',
                                      params: { invoiceId: inv.id },
                                    })
                                  }
                                  sx={{ cursor: 'pointer' }}
                                />
                              ),
                            )}
                          </MDBox>
                        </MDBox>
                      )}

                    {/* Extraction Results */}
                    {hasExtractions && (
                      <MDBox>
                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          color="text.secondary"
                        >
                          Extraction Attempts
                        </MDTypography>
                        <MDBox
                          display="flex"
                          flexDirection="column"
                          gap={0.5}
                          mt={1}
                        >
                          {detailedEmail?.extractionResults?.apInvoices?.map(
                            (ex) => (
                              <MDBox key={ex.id} mb={1}>
                                <MDTypography
                                  variant="caption"
                                  color={
                                    ex.status === 'failed'
                                      ? 'error'
                                      : 'text.secondary'
                                  }
                                  fontWeight={
                                    ex.status === 'failed'
                                      ? 'medium'
                                      : 'regular'
                                  }
                                >
                                  • AP Invoice Extraction ({ex.status})
                                  {ex.apInvoiceId && ` → Invoice Created`}
                                </MDTypography>
                                {ex.status === 'failed' && ex.errorDetails && (
                                  <MDTypography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                      display: 'block',
                                      ml: 2,
                                      mt: 0.5,
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    {typeof ex.errorDetails === 'object' &&
                                    ex.errorDetails !== null
                                      ? (
                                          ex.errorDetails as {
                                            message?: string;
                                          }
                                        ).message ||
                                        JSON.stringify(ex.errorDetails)
                                      : String(ex.errorDetails)}
                                  </MDTypography>
                                )}
                              </MDBox>
                            ),
                          )}
                          {detailedEmail?.extractionResults?.quoteSheets?.map(
                            (ex) => (
                              <MDBox key={ex.id} mb={1}>
                                <MDTypography
                                  variant="caption"
                                  color={
                                    ex.status === 'failed'
                                      ? 'error'
                                      : 'text.secondary'
                                  }
                                  fontWeight={
                                    ex.status === 'failed'
                                      ? 'medium'
                                      : 'regular'
                                  }
                                >
                                  • Quote Sheet Extraction ({ex.status})
                                  {ex.quoteSheetId && ` → Quote Sheet Created`}
                                </MDTypography>
                                {ex.status === 'failed' && ex.errorDetails && (
                                  <MDTypography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                      display: 'block',
                                      ml: 2,
                                      mt: 0.5,
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    {typeof ex.errorDetails === 'object' &&
                                    ex.errorDetails !== null
                                      ? (
                                          ex.errorDetails as {
                                            message?: string;
                                          }
                                        ).message ||
                                        JSON.stringify(ex.errorDetails)
                                      : String(ex.errorDetails)}
                                  </MDTypography>
                                )}
                              </MDBox>
                            ),
                          )}
                          {detailedEmail?.extractionResults?.purchaseOrders?.map(
                            (ex) => (
                              <MDBox key={ex.id} mb={1}>
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                >
                                  <MDTypography
                                    variant="caption"
                                    color={
                                      ex.status === 'failed'
                                        ? 'error'
                                        : 'text.secondary'
                                    }
                                    fontWeight={
                                      ex.status === 'failed'
                                        ? 'medium'
                                        : 'regular'
                                    }
                                  >
                                    • Purchase Order Extraction ({ex.status})
                                    {ex.stagedPurchaseOrderId && ` → PO Staged`}
                                  </MDTypography>
                                  {ex.status === 'failed' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={handleRetryPOExtraction}
                                      disabled={retryPOExtraction.isPending}
                                    >
                                      <Icon sx={{ fontSize: '1rem', mr: 0.5 }}>
                                        refresh
                                      </Icon>
                                      Retry
                                    </Button>
                                  )}
                                </MDBox>
                                {ex.status === 'failed' && ex.errorDetails && (
                                  <MDTypography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                      display: 'block',
                                      ml: 2,
                                      mt: 0.5,
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    {typeof ex.errorDetails === 'object' &&
                                    ex.errorDetails !== null
                                      ? (
                                          ex.errorDetails as {
                                            message?: string;
                                          }
                                        ).message ||
                                        JSON.stringify(ex.errorDetails)
                                      : String(ex.errorDetails)}
                                  </MDTypography>
                                )}
                              </MDBox>
                            ),
                          )}
                        </MDBox>
                      </MDBox>
                    )}
                  </MDBox>
                </AccordionDetails>
              </Accordion>
            </MDBox>
          )}

          {currentUser?.isAdmin &&
            workflowRunsQuery.data &&
            workflowRunsQuery.data.runs.length > 0 && (
              <MDBox mb={2}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ color: 'primary.main' }}>account_tree</Icon>
                      <MDTypography variant="subtitle2" fontWeight="medium">
                        Workflow Runs
                      </MDTypography>
                      <Chip
                        label={workflowRunsQuery.data.runs.length}
                        size="small"
                        color="primary"
                        sx={{ ml: 0.5 }}
                      />
                    </MDBox>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {workflowRunsQuery.data.runs.map((item) => (
                        <Link
                          key={item.run.id}
                          to="/workflows/$workflowDefinitionId/runs"
                          params={{
                            workflowDefinitionId: item.run.workflowDefinitionId,
                          }}
                          className="block rounded p-2 hover:bg-gray-100"
                        >
                          <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <MDTypography variant="body2" fontWeight="medium">
                              {item.workflowDefinitionName}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              className={cn(
                                item.run.status === 'completed' &&
                                  'text-green-600',
                                item.run.status === 'running' &&
                                  'text-blue-600',
                                item.run.status === 'failed' && 'text-red-600',
                                item.run.status === 'cancelled' &&
                                  'text-gray-500',
                              )}
                            >
                              {item.run.status}
                            </MDTypography>
                          </MDBox>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            {new Date(item.run.triggeredAt).toLocaleString()}
                          </MDTypography>
                        </Link>
                      ))}
                    </MDBox>
                  </AccordionDetails>
                </Accordion>
              </MDBox>
            )}

          <Divider sx={{ my: 3 }} />

          {/* Email Body Section */}
          <MDBox
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              p: 3,
              border: '2px solid',
              borderColor: 'primary.light',
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1} mb={2}>
              <Icon sx={{ color: 'primary.main' }}>email</Icon>
              <MDTypography variant="h6" fontWeight="medium">
                Email Content
              </MDTypography>
            </MDBox>
            {emailToDisplay.htmlContent ? (
              (() => {
                // Replace cid: references with actual attachment URLs
                let processedHtml = emailToDisplay.htmlContent;

                if (emailToDisplay.attachments?.length) {
                  emailToDisplay.attachments.forEach((attachment) => {
                    let replaced = false;

                    // Try matching by contentId first (if available)
                    if (attachment.contentId) {
                      const cidPattern = new RegExp(
                        `cid:${attachment.contentId}`,
                        'gi',
                      );
                      const beforeReplace = processedHtml;
                      processedHtml = processedHtml.replace(
                        cidPattern,
                        attachment.s3Url,
                      );
                      if (beforeReplace !== processedHtml) {
                        replaced = true;
                      }
                    }

                    // Try matching by attachment ID (UUIDs in cid:)
                    if (!replaced) {
                      const cidPattern = new RegExp(
                        `cid:${attachment.id}`,
                        'gi',
                      );
                      const beforeReplace = processedHtml;
                      processedHtml = processedHtml.replace(
                        cidPattern,
                        attachment.s3Url,
                      );
                      if (beforeReplace !== processedHtml) {
                        replaced = true;
                      }
                    }

                    // Fallback: Match by filename pattern (e.g., cid:image003.png@... matches image003.png)
                    if (!replaced) {
                      const escapedFilename = attachment.originalName.replace(
                        /[.*+?^${}()|[\]\\]/g,
                        '\\$&',
                      );
                      const cidPattern = new RegExp(
                        `cid:${escapedFilename}@[^"'\\s>]+`,
                        'gi',
                      );
                      processedHtml = processedHtml.replace(
                        cidPattern,
                        attachment.s3Url,
                      );
                    }
                  });
                }

                return (
                  <Box
                    sx={{
                      maxHeight: '50vh',
                      overflow: 'auto',
                      '& img': { maxWidth: '100%' },
                    }}
                    dangerouslySetInnerHTML={{ __html: processedHtml }}
                  />
                );
              })()
            ) : emailToDisplay.textContent ? (
              <Box
                sx={{
                  maxHeight: '50vh',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                }}
              >
                {emailToDisplay.textContent}
              </Box>
            ) : (
              <MDTypography variant="body2" color="text.secondary">
                (No content)
              </MDTypography>
            )}
          </MDBox>

          {emailToDisplay.attachments &&
            emailToDisplay.attachments.length > 0 && (
              <>
                <Divider sx={{ mt: 3, mb: 2 }} />
                <MDBox
                  sx={{
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                    <Icon sx={{ color: 'primary.main' }}>attach_file</Icon>
                    <MDTypography variant="h6" fontWeight="medium">
                      Attachments
                    </MDTypography>
                    <Chip
                      label={emailToDisplay.attachments.length}
                      size="small"
                      color="primary"
                      sx={{ ml: 0.5 }}
                    />
                  </MDBox>

                  {/* Render all attachments sequentially */}
                  {emailToDisplay.attachments.map((attachment, index) => {
                    // Check file extension as fallback for generic MIME types
                    const fileName = attachment.originalName.toLowerCase();
                    const isPDFByExtension = fileName.endsWith('.pdf');
                    const isImageByExtension =
                      /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(fileName);

                    const isExcelByExtension = /\.(xlsx|xls)$/.test(fileName);

                    const isImage =
                      attachment.mimeType.startsWith('image/') ||
                      isImageByExtension;
                    const isPDF =
                      attachment.mimeType.includes('pdf') || isPDFByExtension;
                    const isExcel =
                      attachment.mimeType.includes('spreadsheet') ||
                      attachment.mimeType.includes('excel') ||
                      isExcelByExtension;

                    const fileSizeKB = Math.round(attachment.sizeBytes / 1024);
                    const fileSizeMB =
                      fileSizeKB > 1024
                        ? (attachment.sizeBytes / (1024 * 1024)).toFixed(2)
                        : null;

                    // Get file extension for display
                    const fileExtension =
                      attachment.originalName.split('.').pop()?.toUpperCase() ||
                      'FILE';
                    const displayType =
                      attachment.mimeType === 'application/octet-stream'
                        ? fileExtension
                        : attachment.mimeType.split('/')[1].toUpperCase();

                    // Get file type icon
                    const getFileIcon = () => {
                      if (isImage) return 'image';
                      if (isPDF) return 'picture_as_pdf';
                      if (isExcel) return 'table_chart';
                      if (
                        fileName.endsWith('.docx') ||
                        fileName.endsWith('.doc')
                      )
                        return 'description';
                      if (
                        fileName.endsWith('.zip') ||
                        fileName.includes('compressed')
                      )
                        return 'folder_zip';
                      return 'insert_drive_file';
                    };

                    return (
                      <Accordion
                        key={attachment.id}
                        defaultExpanded
                        sx={{
                          mb:
                            index <
                            (emailToDisplay.attachments?.length ?? 0) - 1
                              ? 2
                              : 0,
                          '&:before': { display: 'none' },
                          boxShadow: 'none',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: '8px !important',
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<Icon>expand_more</Icon>}
                          sx={{
                            bgcolor: 'background.paper',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <MDBox
                            display="flex"
                            alignItems="center"
                            gap={2}
                            width="100%"
                            pr={2}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1,
                                bgcolor: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Icon
                                sx={{
                                  color: 'primary.main',
                                  fontSize: '1.25rem',
                                }}
                              >
                                {getFileIcon()}
                              </Icon>
                            </Box>
                            <Box flex={1} minWidth={0}>
                              <MDTypography
                                variant="body2"
                                fontWeight="medium"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {attachment.originalName}
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                {displayType} •{' '}
                                {fileSizeMB
                                  ? `${fileSizeMB} MB`
                                  : `${fileSizeKB} KB`}
                              </MDTypography>
                            </Box>
                            <Tooltip title="Open in new tab">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(attachment.s3Url, '_blank');
                                }}
                                sx={{ color: 'primary.main' }}
                              >
                                <Icon>open_in_new</Icon>
                              </IconButton>
                            </Tooltip>
                          </MDBox>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          {/* Embedded viewer */}
                          <Box
                            sx={{
                              bgcolor: 'background.paper',
                            }}
                          >
                            {isImage ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  p: 2,
                                  maxHeight: '600px',
                                  overflow: 'auto',
                                }}
                              >
                                <img
                                  src={attachment.s3Url}
                                  alt={attachment.originalName}
                                  style={{
                                    maxWidth: '100%',
                                    height: 'auto',
                                    borderRadius: '4px',
                                  }}
                                />
                              </Box>
                            ) : isPDF ? (
                              <Box sx={{ p: 2 }}>
                                <PDFViewer url={attachment.s3Url} width={800} />
                              </Box>
                            ) : isExcel ? (
                              <Box sx={{ p: 2 }}>
                                <XlsxViewer
                                  url={attachment.s3Url}
                                  fileName={attachment.originalName}
                                />
                              </Box>
                            ) : (
                              <MDBox
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                p={4}
                                gap={2}
                              >
                                <Icon
                                  sx={{
                                    fontSize: '4rem',
                                    color: 'text.secondary',
                                  }}
                                >
                                  insert_drive_file
                                </Icon>
                                <MDTypography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Preview not available for this file type
                                </MDTypography>
                                <Tooltip title="Download or open in new tab to view">
                                  <IconButton
                                    onClick={() =>
                                      window.open(attachment.s3Url, '_blank')
                                    }
                                    sx={{
                                      bgcolor: 'primary.main',
                                      color: 'white',
                                      '&:hover': {
                                        bgcolor: 'primary.dark',
                                      },
                                    }}
                                  >
                                    <Icon>download</Icon>
                                  </IconButton>
                                </Tooltip>
                              </MDBox>
                            )}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </MDBox>
              </>
            )}
        </MDBox>
      </DialogContent>
    </Dialog>
  );
};

export default InboundEmailModal;
